/**
 * LifeEventsCard Component
 * Single Responsibility: Display and manage life events from meeting
 */

import React from 'react'
import { CalendarDays } from 'lucide-react'
import { LifeEvent } from '../../../types/meeting'
import LifeEventItem from './LifeEventItem'

interface LifeEventsCardProps {
  events: LifeEvent[]
  onAddEvent: () => void
  onEditEvent: (event: LifeEvent) => void
  onDeleteEvent: (eventId: string) => void
}

const LifeEventsCard: React.FC<LifeEventsCardProps> = ({
  events,
  onAddEvent,
  onEditEvent,
  onDeleteEvent
}) => {
  // Sort events by date (most recent first)
  const sortedEvents = [...events].sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime()
  })

  return (
    <div className="bg-white rounded-xl p-6 border border-slate-200">
      <div className="flex justify-between items-center mb-4 pb-2 border-b border-slate-200">
        <h3 className="text-lg font-semibold text-kielis-navy flex items-center gap-2">
          <CalendarDays className="w-5 h-5" />
          Life Events
        </h3>
        <button
          onClick={onAddEvent}
          className="px-3 py-1.5 bg-kielis-navy text-white rounded-lg text-sm hover:bg-slate-800 transition-all"
        >
          + Add Event
        </button>
      </div>

      {sortedEvents.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <div className="text-4xl mb-2">📅</div>
          <p className="text-sm">No life events detected yet.</p>
          <p className="text-xs mt-1">Life events will appear here from the meeting transcription.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Timeline indicator */}
          <div className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-2">
            Timeline
          </div>

          {/* Events list */}
          {sortedEvents.map((event) => (
            <LifeEventItem
              key={event.id}
              event={event}
              onEdit={onEditEvent}
              onDelete={onDeleteEvent}
            />
          ))}

          {/* Summary stats */}
          <div className="mt-4 pt-3 border-t border-slate-200">
            <div className="flex justify-between text-xs text-slate-500">
              <span>Total Events: {events.length}</span>
              <span>
                Auto-detected: {events.filter(e => e.source === 'transcription').length} |
                Manual: {events.filter(e => e.source === 'manual').length}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LifeEventsCard